import React, { useState, useEffect } from 'react';
import { Linkedin, Mail } from 'lucide-react';
import { getTeamMembers } from '../api/api';

const Team = () => {
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTeamMembers = async () => {
      try {
        const data = await getTeamMembers();
        setTeamMembers(data);
      } catch (error) {
        console.error('Error loading team members:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchTeamMembers();
  }, []);

  return (
    <section id="team" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl lg:text-5xl font-bold text-black mb-4">
            Our <span className="text-yellow-600">Team</span>
          </h2>
          <div className="w-24 h-1 bg-yellow-600 mx-auto mb-6"></div>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Meet our talented team of architects, designers, and project managers with high proficiency in team management and project coordination, combined with fresh ideas and innovative concepts.
          </p>
        </div>

        {/* Team Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
          {loading ? (
            <div className="col-span-full text-center py-12">
              <div className="inline-block w-12 h-12 border-4 border-yellow-600 border-t-transparent rounded-full animate-spin"></div>
              <p className="mt-4 text-gray-600">Loading team members...</p>
            </div>
          ) : (
            teamMembers.map((member) => (
              <div
                key={member.id}
                className="group bg-white rounded-sm shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-500 hover:-translate-y-2"
              >
                {/* Image */}
                <div className="relative h-80 overflow-hidden bg-gray-100">
                  <img
                    src={member.image_url}
                    alt={member.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                  
                  {/* Social Icons - shown on hover */}
                  {(member.email || member.linkedin) && (
                    <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-3 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                      {member.email && (
                        <a href={`mailto:${member.email}`} className="w-10 h-10 bg-white rounded-full flex items-center justify-center hover:bg-yellow-600 hover:text-white transition-colors">
                          <Mail size={18} />
                        </a>
                      )}
                      {member.linkedin && (
                        <a href={member.linkedin} target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white rounded-full flex items-center justify-center hover:bg-yellow-600 hover:text-white transition-colors">
                          <Linkedin size={18} />
                        </a>
                      )}
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="p-6 text-center">
                  <h3 className="text-xl font-bold text-black mb-1">{member.name}</h3>
                  <p className="text-yellow-600 font-semibold text-sm uppercase tracking-wide">{member.role}</p>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Team Values */}
        <div className="mt-20 bg-gray-50 rounded-sm p-12 text-center">
          <h3 className="text-3xl font-bold text-black mb-6">Join Our Team</h3>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto mb-8">
            We're always looking for talented architects, designers, and creative professionals who are passionate about creating exceptional spaces. If you have the drive and vision, we'd love to hear from you.
          </p>
          <button
            onClick={() => {
              const element = document.querySelector('#contact');
              if (element) element.scrollIntoView({ behavior: 'smooth' });
            }}
            className="px-8 py-4 bg-black text-white font-semibold rounded-sm hover:bg-gray-800 transition-all duration-300 shadow-lg hover:shadow-xl"
          >
            Contact Us
          </button>
        </div>
      </div>
    </section>
  );
};

export default Team;